import React, { useState, useEffect } from 'react';
import { apiClient } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock, 
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Video,
  User,
  ExternalLink,
  DollarSign,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, addDays, subDays } from 'date-fns';
import NewAppointmentForm from '../components/forms/NewAppointmentForm';

const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate, statusFilter, typeFilter]);

  const fetchAppointments = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      let url = `/appointments?date=${dateStr}`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (typeFilter !== 'all') {
        url += `&appointment_type=${typeFilter}`;
      }
      const response = await apiClient.get(url);
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appointmentId, status) => {
    try {
      await apiClient.put(`/appointments/${appointmentId}`, { status });
      toast.success(`Status updated to ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const startTeleconsult = async (appt) => {
    try {
      const response = await apiClient.post(`/teleconsult/${appt.appointment_id}/start`);
      if (response.data.payment_warning) {
        toast.warning('Payment pending - proceeding with consultation');
      }
      if (response.data.video_link) {
        window.open(response.data.video_link, '_blank');
      } else {
        toast.info('No video link set. Please add a video link first.');
      }
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to start consultation');
    }
  };

  const endTeleconsult = async (appointmentId) => {
    try {
      await apiClient.post(`/teleconsult/${appointmentId}/end`);
      toast.success('Consultation completed');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to end consultation');
    }
  };

  const markNoShow = async (appointmentId) => {
    try {
      await apiClient.post(`/teleconsult/${appointmentId}/no-show`);
      toast.success('Marked as no-show');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updatePayment = async (appointmentId, status) => {
    try {
      await apiClient.put(`/teleconsult/${appointmentId}/payment?payment_status=${status}`);
      toast.success(`Payment marked as ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      waiting: { class: 'status-waiting', label: 'Waiting' },
      in_consultation: { class: 'status-in-consultation', label: 'In Consultation' },
      done: { class: 'status-done', label: 'Done' },
      no_show: { class: 'status-no-show', label: 'No Show' },
    };
    const { class: className, label } = config[status] || config.waiting;
    return <Badge className={className}>{label}</Badge>;
  };

  const getPaymentBadge = (status) => {
    if (!status) return null;
    const config = {
      pending: { class: 'bg-amber-100 text-amber-800', label: 'Payment Pending' },
      paid: { class: 'bg-green-100 text-green-800', label: 'Paid' },
      waived: { class: 'bg-gray-100 text-gray-800', label: 'Waived' },
    };
    const { class: className, label } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const PatientAvatar = ({ photo, name }) => (
    photo ? (
      <img 
        src={`data:image/png;base64,${photo}`} 
        alt={name}
        className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm"
      />
    ) : (
      <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-medium text-lg">
        {name?.charAt(0) || '?'}
      </div>
    )
  );

  const inPersonAppts = appointments.filter(a => a.appointment_type !== 'online');
  const onlineAppts = appointments.filter(a => a.appointment_type === 'online');

  const stats = {
    total: appointments.length,
    waiting: appointments.filter(a => a.status === 'waiting').length,
    inConsultation: appointments.filter(a => a.status === 'in_consultation').length,
    done: appointments.filter(a => a.status === 'done').length,
    online: onlineAppts.length,
  };

  const formatCurrency = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const renderAppointmentCard = (appt, index) => {
    const isOnline = appt.appointment_type === 'online';
    
    return (
      <Card 
        key={appt.appointment_id}
        className={`card-hover animate-fade-in ${isOnline ? 'border-l-4 border-l-blue-500' : ''}`}
        style={{ animationDelay: `${index * 0.05}s` }}
        data-testid={`appointment-${appt.appointment_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <PatientAvatar photo={appt.patient_photo} name={appt.patient_name} />
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium">{appt.patient_name}</h3>
                {isOnline && (
                  <Badge className="bg-blue-100 text-blue-800">
                    <Video className="w-3 h-3 mr-1" />
                    Online
                  </Badge>
                )}
                {getStatusBadge(appt.status)}
                {isOnline && getPaymentBadge(appt.payment_status)}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {appt.time}
                </span>
                {appt.reason && (
                  <span className="truncate">{appt.reason}</span>
                )}
                {isOnline && appt.symptoms && (
                  <span className="truncate text-blue-600">{appt.symptoms}</span>
                )}
                {isOnline && appt.consultation_fee && (
                  <span className="flex items-center gap-1 text-green-600">
                    <DollarSign className="w-3 h-3" />
                    {formatCurrency(appt.consultation_fee)}
                  </span>
                )}
                {isOnline && appt.intake_attachments?.length > 0 && (
                  <span className="flex items-center gap-1 text-purple-600">
                    <Paperclip className="w-3 h-3" />
                    {appt.intake_attachments.length} files
                  </span>
                )}
              </div>
            </div>

            {/* Status Actions */}
            <div className="flex items-center gap-2">
              {/* View Details Button for Online */}
              {isOnline && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedAppointment(appt);
                    setShowDetails(true);
                  }}
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              )}
              
              {isOnline ? (
                // Online consultation actions
                <>
                  {appt.status === 'waiting' && (
                    <>
                      {appt.payment_status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updatePayment(appt.appointment_id, 'paid')}
                          className="text-green-600"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => startTeleconsult(appt)}
                        data-testid={`start-teleconsult-${appt.appointment_id}`}
                      >
                        <Video className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markNoShow(appt.appointment_id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {appt.status === 'in_consultation' && (
                    <>
                      {appt.video_link && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(appt.video_link, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Open Video
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => endTeleconsult(appt.appointment_id)}
                        data-testid={`end-teleconsult-${appt.appointment_id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Complete
                      </Button>
                    </>
                  )}
                  {appt.status === 'no_show' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => updateStatus(appt.appointment_id, 'waiting')}
                    >
                      Restore
                    </Button>
                  )}
                </>
              ) : (
                // In-person actions
                <>
                  {appt.status === 'waiting' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(appt.appointment_id, 'in_consultation')}
                        data-testid={`start-consultation-${appt.appointment_id}`}
                      >
                        Start
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateStatus(appt.appointment_id, 'no_show')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {appt.status === 'in_consultation' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(appt.appointment_id, 'done')}
                      data-testid={`complete-${appt.appointment_id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Done
                    </Button>
                  )}
                  {appt.status === 'no_show' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => updateStatus(appt.appointment_id, 'waiting')}
                    >
                      Restore
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Queue & Appointments</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
        <Button 
          onClick={() => setShowNewAppointment(true)}
          className="rounded-full"
          data-testid="add-appointment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Appointment
        </Button>
      </div>

      {/* Date Navigation */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setSelectedDate(subDays(selectedDate, 1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-44" data-testid="date-picker-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(selectedDate, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setSelectedDate(addDays(selectedDate, 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Button 
          variant="ghost" 
          onClick={() => setSelectedDate(new Date())}
          className="text-primary"
        >
          Today
        </Button>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="in_person">In-Person</SelectItem>
            <SelectItem value="online">Online</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="waiting">Waiting</SelectItem>
            <SelectItem value="in_consultation">In Consultation</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="no_show">No Show</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.waiting}</p>
            <p className="text-xs text-muted-foreground">Waiting</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.inConsultation}</p>
            <p className="text-xs text-muted-foreground">In Consultation</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.done}</p>
            <p className="text-xs text-muted-foreground">Done</p>
          </CardContent>
        </Card>
        <Card className="border-violet-200 bg-violet-50/50">
          <CardContent className="p-4 text-center">
            <Video className="w-4 h-4 text-violet-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-violet-600">{stats.online}</p>
            <p className="text-xs text-muted-foreground">Online</p>
          </CardContent>
        </Card>
      </div>

      {/* Appointments List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : appointments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No appointments for this date</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowNewAppointment(true)}
            >
              Schedule an appointment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {appointments.map((appt, index) => renderAppointmentCard(appt, index))}
        </div>
      )}

      {/* New Appointment Dialog */}
      <Dialog open={showNewAppointment} onOpenChange={setShowNewAppointment}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Appointment</DialogTitle>
          </DialogHeader>
          <NewAppointmentForm 
            defaultDate={format(selectedDate, 'yyyy-MM-dd')}
            onSuccess={() => {
              setShowNewAppointment(false);
              fetchAppointments();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Teleconsult Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-600" />
              Online Consultation Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-6">
              {/* Patient Info */}
              <div className="flex items-center gap-4">
                <PatientAvatar 
                  photo={selectedAppointment.patient_photo} 
                  name={selectedAppointment.patient_name} 
                />
                <div>
                  <h3 className="font-medium text-lg">{selectedAppointment.patient_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedAppointment.date} at {selectedAppointment.time}
                  </p>
                </div>
                <div className="ml-auto flex gap-2">
                  {getStatusBadge(selectedAppointment.status)}
                  {getPaymentBadge(selectedAppointment.payment_status)}
                </div>
              </div>

              {/* Intake Information */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Symptoms</p>
                    <p className="text-sm">{selectedAppointment.symptoms || 'Not provided'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Duration</p>
                    <p className="text-sm">{selectedAppointment.symptom_duration || 'Not provided'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Current Medications</p>
                    <p className="text-sm">{selectedAppointment.current_medications || 'None reported'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Consultation Fee</p>
                    <p className="text-sm font-medium text-green-600">
                      {selectedAppointment.consultation_fee ? formatCurrency(selectedAppointment.consultation_fee) : 'Not set'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Video Link */}
              {selectedAppointment.video_link && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Video Link</p>
                        <p className="text-sm text-blue-600 truncate max-w-md">{selectedAppointment.video_link}</p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => window.open(selectedAppointment.video_link, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open Video
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Attachments */}
              {selectedAppointment.intake_attachments?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Patient Attachments ({selectedAppointment.intake_attachments.length})
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedAppointment.intake_attachments.map((att, index) => (
                      <div 
                        key={att.attachment_id || index}
                        className="border rounded-lg overflow-hidden hover:border-primary transition-colors cursor-pointer"
                        onClick={() => {
                          // Open attachment in new tab
                          const dataUrl = att.content_type.startsWith('image/') 
                            ? `data:${att.content_type};base64,${att.data}`
                            : `data:${att.content_type};base64,${att.data}`;
                          const newWindow = window.open();
                          if (newWindow) {
                            if (att.content_type.startsWith('image/')) {
                              newWindow.document.write(`<img src="${dataUrl}" style="max-width: 100%;" />`);
                            } else {
                              newWindow.document.write(`<iframe src="${dataUrl}" style="width: 100%; height: 100vh; border: none;"></iframe>`);
                            }
                          }
                        }}
                      >
                        {att.content_type?.startsWith('image/') ? (
                          <img 
                            src={`data:${att.content_type};base64,${att.data}`}
                            alt={att.filename}
                            className="w-full h-32 object-cover"
                          />
                        ) : (
                          <div className="w-full h-32 bg-red-50 flex flex-col items-center justify-center">
                            <FileText className="w-8 h-8 text-red-600 mb-2" />
                            <span className="text-xs text-muted-foreground">PDF Document</span>
                          </div>
                        )}
                        <div className="p-2 bg-muted/50">
                          <p className="text-xs truncate">{att.filename}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consent Info */}
              {selectedAppointment.teleconsult_consent && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm">
                  <p className="font-medium text-green-800">Patient consent received</p>
                  <p className="text-green-600 text-xs mt-1">
                    Patient agreed to receive medical consultation through online communication.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
